import { useEffect, useState, type ReactElement } from "react";
import { Save, Unlink } from "lucide-react";
import { Badge, Button, FormField, Select, Stack, Surface, Text } from "@ux/primitives";
import { BottomActionBar, Toolbar, UserPicker, WorkspacePicker } from "@ux/compositions";
import { useViewport } from "@ux/hooks";
import { palette, space } from "@ux/tokens";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { formatActor } from "../common/formatting";
import { AssetName } from "../assets/AssetName";
import { UserName } from "../users/UserName";
import { WorkspaceName } from "../workspaces/WorkspaceName";
import type { AssetView } from "../assets/types";
import type { UserDirectoryEntry } from "../users/types";
import type { WorkspaceView } from "../workspaces/types";
import {
    targetKinds,
    type AssetAssignmentCreatePayload,
    type AssetAssignmentView,
    type AssignmentTargetKind,
} from "./types";

export type AssetAssignmentFormMode =
    | { kind: "create" }
    | { kind: "view"; record: AssetAssignmentView };

export interface AssetAssignmentFormProps {
    readonly mode: AssetAssignmentFormMode;
    readonly assets: readonly AssetView[];
    readonly onCreate: (payload: AssetAssignmentCreatePayload) => void;
    readonly onRelease: (record: AssetAssignmentView) => void;
    readonly onCancel: () => void;
    readonly isSaving: boolean;
    readonly errorMessage: string | null;
}

interface CreateState {
    readonly id: string;
    readonly assetId: string;
    readonly targetKind: AssignmentTargetKind | "";
    readonly user: UserDirectoryEntry | null;
    readonly workspace: WorkspaceView | null;
}

export function AssetAssignmentForm({
    mode,
    assets,
    onCreate,
    onRelease,
    onCancel,
    isSaving,
    errorMessage,
}: AssetAssignmentFormProps): ReactElement {
    const { isPhone } = useViewport();
    const canEdit = useCanEdit();
    const [state, setState] = useState<CreateState>(() => initialCreateState());
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        if (mode.kind === "create") {
            setState(initialCreateState());
        }
        setValidationError(null);
    }, [mode]);

    if (mode.kind === "view") {
        return <AssignmentDetail record={mode.record} onRelease={onRelease} onCancel={onCancel} isSaving={isSaving} />;
    }

    function handleSubmit(): void {
        if (state.targetKind === "") {
            setValidationError("Pick a target kind.");
            return;
        }
        if (state.assetId === "") {
            setValidationError("Pick an asset.");
            return;
        }
        if (state.targetKind === "User" && state.user === null) {
            setValidationError("Pick a user.");
            return;
        }
        if (state.targetKind === "Workspace" && state.workspace === null) {
            setValidationError("Pick a workspace.");
            return;
        }

        const payload: AssetAssignmentCreatePayload = {
            id: state.id,
            assetId: state.assetId,
            targetKind: state.targetKind,
            userId: state.targetKind === "User" && state.user !== null ? state.user.id : null,
            workspaceId:
                state.targetKind === "Workspace" && state.workspace !== null
                    ? state.workspace.id
                    : null,
        };

        setValidationError(null);
        onCreate(payload);
    }

    const availableAssets = assets.filter(
        (a) => !a.isDeleted && (a.currentAssignmentId === null || a.currentAssignmentId === undefined),
    );

    const createActionButtons = (
        <>
            <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
                Cancel
            </Button>
            {canEdit ? (
                <Button
                    variant="primary"
                    leadingIcon={<Save size={16} />}
                    onClick={handleSubmit}
                    disabled={isSaving}
                >
                    {isSaving ? "Saving…" : "Assign"}
                </Button>
            ) : null}
        </>
    );

    return (
        <Stack direction="column" gap={0} grow>
            <Toolbar
                leading={<Text role="heading">New assignment</Text>}
                trailing={
                    isPhone ? null : (
                        <Stack direction="row" gap={2} wrap>
                            {createActionButtons}
                        </Stack>
                    )
                }
            />

            <div style={{ flex: 1, overflow: "auto", padding: space[5] }}>
                <Stack gap={4} style={{ maxWidth: 640 }}>
                    {validationError !== null ? (
                        <Surface tone="raised" padding={3}>
                            <Text role="muted" color={palette.danger}>
                                {validationError}
                            </Text>
                        </Surface>
                    ) : null}
                    {errorMessage !== null ? (
                        <Surface tone="raised" padding={3}>
                            <Text role="muted" color={palette.danger}>
                                {errorMessage}
                            </Text>
                        </Surface>
                    ) : null}

                    <FormField
                        label="Asset"
                        required
                        hint="Only unassigned, active assets are listed."
                    >
                        {(id) => (
                            <Select
                                id={id}
                                value={state.assetId}
                                onChange={(e) => setState((p) => ({ ...p, assetId: e.target.value }))}
                                options={availableAssets.map((a) => ({
                                    value: a.id,
                                    label:
                                        a.assetTag !== null &&
                                        a.assetTag !== undefined &&
                                        a.assetTag !== ""
                                            ? `${a.assetTag} · ${a.type} · ${a.make} ${a.model}`
                                            : `${a.type} · ${a.make} ${a.model} · SN ${a.serialNumber}`,
                                }))}
                                placeholder="Select an asset"
                            />
                        )}
                    </FormField>

                    <FormField label="Assign to" required>
                        {(id) => (
                            <Select
                                id={id}
                                value={state.targetKind}
                                onChange={(e) =>
                                    setState((p) => ({
                                        ...p,
                                        targetKind: e.target.value as AssignmentTargetKind | "",
                                    }))
                                }
                                options={targetKinds.map((k) => ({ value: k, label: k }))}
                                placeholder="User or workspace"
                            />
                        )}
                    </FormField>

                    {state.targetKind === "User" ? (
                        <FormField
                            label="User"
                            required
                            hint="Search the Galileo HR directory by name or email."
                        >
                            {(id) => (
                                <UserPicker
                                    id={id}
                                    value={state.user}
                                    onChange={(user) => setState((p) => ({ ...p, user }))}
                                />
                            )}
                        </FormField>
                    ) : null}

                    {state.targetKind === "Workspace" ? (
                        <FormField
                            label="Workspace"
                            required
                            hint="Type to search by name or friendly name."
                        >
                            {(id) => (
                                <WorkspacePicker
                                    id={id}
                                    value={state.workspace}
                                    onChange={(workspace) => setState((p) => ({ ...p, workspace }))}
                                />
                            )}
                        </FormField>
                    ) : null}
                </Stack>
            </div>

            {isPhone ? <BottomActionBar>{createActionButtons}</BottomActionBar> : null}
        </Stack>
    );
}

interface AssignmentDetailProps {
    readonly record: AssetAssignmentView;
    readonly onRelease: (record: AssetAssignmentView) => void;
    readonly onCancel: () => void;
    readonly isSaving: boolean;
}

function AssignmentDetail({
    record,
    onRelease,
    onCancel,
    isSaving,
}: AssignmentDetailProps): ReactElement {
    const { isPhone } = useViewport();
    const canEdit = useCanEdit();

    const detailActionButtons = (
        <>
            <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
                Close
            </Button>
            {canEdit && record.isOpen ? (
                <Button
                    variant="danger"
                    leadingIcon={<Unlink size={16} />}
                    onClick={() => onRelease(record)}
                    disabled={isSaving}
                >
                    {isSaving ? "Releasing…" : "Release"}
                </Button>
            ) : null}
        </>
    );

    return (
        <Stack direction="column" gap={0} grow>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center">
                        <Text role="heading">Assignment detail</Text>
                        {record.isOpen ? (
                            <Badge tone="accent">Open</Badge>
                        ) : (
                            <Badge tone="neutral">Released</Badge>
                        )}
                    </Stack>
                }
                trailing={
                    isPhone ? null : (
                        <Stack direction="row" gap={2} wrap>
                            {detailActionButtons}
                        </Stack>
                    )
                }
            />
            <div style={{ flex: 1, overflow: "auto", padding: space[5] }}>
                <Stack gap={3} style={{ maxWidth: 640 }}>
                    <DetailRow label="Assignment id" value={record.id} mono />
                    <Stack gap={1}>
                        <Text role="eyebrow">Asset</Text>
                        <Text role="body">
                            <AssetName id={record.assetId} />
                        </Text>
                    </Stack>
                    <DetailRow label="Target" value={record.targetKind} />
                    {record.assignedToUserId !== undefined && record.assignedToUserId !== null ? (
                        <Stack gap={1}>
                            <Text role="eyebrow">User</Text>
                            <Text role="body">
                                <UserName id={record.assignedToUserId} />
                            </Text>
                            <DetailRow label="User ID" value={record.assignedToUserId} mono />
                        </Stack>
                    ) : null}
                    {record.assignedToWorkspaceId !== undefined &&
                    record.assignedToWorkspaceId !== null ? (
                        <Stack gap={1}>
                            <Text role="eyebrow">Workspace</Text>
                            <Text role="body">
                                <WorkspaceName id={record.assignedToWorkspaceId} />
                            </Text>
                            <DetailRow
                                label="Workspace ID"
                                value={record.assignedToWorkspaceId}
                                mono
                            />
                        </Stack>
                    ) : null}
                    <Surface tone="raised" padding={4}>
                        <Stack gap={2}>
                            <Text role="eyebrow">Audit</Text>
                            <Text role="muted">Assigned — {formatActor(record.assigned)}</Text>
                            <Text role="muted">
                                Released —{" "}
                                {record.released !== undefined && record.released !== null
                                    ? formatActor(record.released)
                                    : "—"}
                            </Text>
                        </Stack>
                    </Surface>
                </Stack>
            </div>

            {isPhone ? <BottomActionBar>{detailActionButtons}</BottomActionBar> : null}
        </Stack>
    );
}

interface DetailRowProps {
    readonly label: string;
    readonly value: string;
    readonly mono?: boolean;
}

function DetailRow({ label, value, mono = false }: DetailRowProps): ReactElement {
    return (
        <Stack gap={1}>
            <Text role="eyebrow">{label}</Text>
            <Text role={mono ? "mono" : "body"}>{value}</Text>
        </Stack>
    );
}

function initialCreateState(): CreateState {
    return {
        id: crypto.randomUUID(),
        assetId: "",
        targetKind: "",
        user: null,
        workspace: null,
    };
}
